import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, LineChart, Line } from "recharts";
import FacultyDrilldown from "../components/FacultyDrilldown";

const PALETTE = { teal:'#1A9E8F', gold:'#C8A63D', green:'#6BAD3D', rose:'#C8553D' };

const DESIG_FULL = { 'Prof':'Professor', 'Assoc Prof':'Associate Professor', 'Asst Prof':'Assistant Professor', 'SR':'Senior Resident' };

export default function Summary() {
  const [stats, setStats] = useState(null);
  const [drill, setDrill] = useState(null); // { department?, designation?, title }

  useEffect(() => {
    let alive = true;
    api.get("/stats/summary")
      .then(({data}) => { if (alive) setStats(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!stats) return <div style={{padding:40,textAlign:'center'}}><div className="spinner"/></div>;

  const desigData = Object.entries(stats.desig_counts).map(([k,v]) => ({ name: k.replace(' Professor','Prof').replace('Senior Resident','SR'), fullName: k, value: v }));

  const trendData = (() => {
    const cur = stats.total_faculty || 0;
    const months = ['Aug','Sep','Oct','Nov','Dec','Jan'];
    const base = Math.max(0, cur - 5);
    return months.map((m,i) => ({ month: m, count: base + i + (i===5 ? Math.max(0, cur - (base+5)) : 0) }));
  })();

  const ringTotal = (stats.type_counts['Pre-Clinical'] || 0) + (stats.type_counts['Para-Clinical'] || 0) + (stats.type_counts['Clinical'] || 0);
  const seg = (count) => ringTotal ? (count / ringTotal) * 91 : 0;

  const actDot = (type) => {
    switch(type) {
      case 'promotion': return PALETTE.green;
      case 'join': return PALETTE.teal;
      case 'salary': return PALETTE.gold;
      case 'abscond': return PALETTE.rose;
      case 'relieve': return PALETTE.rose;
      default: return PALETTE.teal;
    }
  };

  return (
    <div data-testid="view-summary">
      <div className="topbar">
        <div>
          <h1 className="pg-title">Executive Summary</h1>
          <div className="pg-sub">Live KPIs derived from 10 normalised tables</div>
        </div>
        <div className="topbar-meta">
          <span className="meta-chip">MP/032/P/3</span>
          <span className="meta-chip">{stats.total_faculty} faculty</span>
        </div>
      </div>

      <div className="grid grid-4" style={{marginBottom:18}}>
        <div className="kpi-card c1" data-testid="kpi-total">
          <div className="kpi-label">Total Faculty</div>
          <div className="kpi-value">{stats.total_faculty}<span className="dn">/ {stats.total_required}</span></div>
          <div className="kpi-foot">{stats.fill_pct}% filled</div>
        </div>
        <div className="kpi-card c2" data-testid="kpi-pg">
          <div className="kpi-label">PG Seats</div>
          <div className="kpi-value">{stats.pg_seats}</div>
          <div className="kpi-foot">12 courses · all granted</div>
        </div>
        <div className="kpi-card c3" data-testid="kpi-met">
          <div className="kpi-label">MET Completion</div>
          <div className="kpi-value">{stats.met_pct}<span className="dn">%</span></div>
          <div className="kpi-foot">{stats.met_pending} pending</div>
        </div>
        <div className="kpi-card c4" data-testid="kpi-bmr">
          <div className="kpi-label">BMR Completion</div>
          <div className="kpi-value">{stats.bmr_pct}<span className="dn">%</span></div>
          <div className="kpi-foot">{stats.bmr_pending} pending</div>
        </div>
      </div>

      <div className="grid grid-3" style={{marginBottom:18}}>
        <div className="card span-2">
          <div className="card-title">Faculty by Designation <span className="badge teal">Live</span> <span style={{fontSize:11,color:'var(--text-3)',fontWeight:400,marginLeft:6}}>click bar to drill down</span></div>
          <div style={{height:200, minHeight:200, width:'100%'}}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={desigData} layout="vertical" margin={{left:30, right:20, top:6, bottom:6}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ECEAE4" horizontal={false} />
                <XAxis type="number" tick={{fill:'#9A9487', fontSize:11}} stroke="#E4DFD6"/>
                <YAxis type="category" dataKey="name" tick={{fill:'#5F5A50', fontSize:12}} stroke="#E4DFD6" width={80}/>
                <Tooltip contentStyle={{background:'#fff', border:'1px solid #E4DFD6', borderRadius:8, fontSize:12}}/>
                <Bar dataKey="value" fill="#1A9E8F" radius={[0,4,4,0]} barSize={18} cursor="pointer"
                  onClick={(data) => { const full = data.fullName || DESIG_FULL[data.name] || data.name; setDrill({ designation: full, title: full }); }}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card">
          <div className="card-title">Department Mix</div>
          <div style={{display:'flex', gap:16, alignItems:'center'}}>
            <svg viewBox="0 0 36 36" style={{width:120, height:120}}>
              <circle cx="18" cy="18" r="14.5" fill="none" stroke="#ECEAE4" strokeWidth="4.5"/>
              <circle cx="18" cy="18" r="14.5" fill="none" stroke={PALETTE.teal} strokeWidth="4.5" strokeDasharray={`${seg(stats.type_counts['Pre-Clinical'])} 91`} transform="rotate(-90 18 18)"/>
              <circle cx="18" cy="18" r="14.5" fill="none" stroke={PALETTE.gold} strokeWidth="4.5" strokeDasharray={`${seg(stats.type_counts['Para-Clinical'])} 91`} strokeDashoffset={`-${seg(stats.type_counts['Pre-Clinical'])}`} transform="rotate(-90 18 18)"/>
              <circle cx="18" cy="18" r="14.5" fill="none" stroke={PALETTE.green} strokeWidth="4.5" strokeDasharray={`${seg(stats.type_counts['Clinical'])} 91`} strokeDashoffset={`-${seg(stats.type_counts['Pre-Clinical']) + seg(stats.type_counts['Para-Clinical'])}`} transform="rotate(-90 18 18)"/>
              <text x="18" y="17.5" textAnchor="middle" fill="#1A1A1A" fontSize="6" fontWeight="500" fontFamily="JetBrains Mono">{ringTotal}</text>
              <text x="18" y="22" textAnchor="middle" fill="#9A9487" fontSize="3.5" fontFamily="Inter">faculty</text>
            </svg>
            <div style={{display:'flex', flexDirection:'column', gap:8, fontSize:11.5, flex:1}}>
              {['Pre-Clinical','Para-Clinical','Clinical'].map((t,i) => (
                <div key={t} style={{display:'flex', alignItems:'center', gap:8}}>
                  <span style={{width:10, height:10, borderRadius:3, background:[PALETTE.teal, PALETTE.gold, PALETTE.green][i]}}/>
                  {t}
                  <span style={{marginLeft:'auto', fontFamily:'var(--mono)', color:'var(--text-2)'}}>{stats.type_counts[t]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="card">
          <div className="card-title">Faculty Count Trend <span className="badge gold">6 months</span></div>
          <div style={{height:180, minHeight:180, width:'100%'}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{left:0, right:10, top:6, bottom:6}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ECEAE4"/>
                <XAxis dataKey="month" tick={{fill:'#9A9487', fontSize:11}} stroke="#E4DFD6"/>
                <YAxis tick={{fill:'#9A9487', fontSize:11}} stroke="#E4DFD6"/>
                <Tooltip contentStyle={{background:'#fff', border:'1px solid #E4DFD6', borderRadius:8, fontSize:12}}/>
                <Line type="monotone" dataKey="count" stroke="#C8A63D" strokeWidth={2.5} dot={{fill:'#C8A63D', r:3}} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card" data-testid="recent-activity">
          <div className="card-title">Recent Activity</div>
          {stats.activity.length === 0 && <div style={{color:'var(--text-3)',fontSize:13}}>No recent events.</div>}
          {stats.activity.map((a,i) => (
            <div className="act-item" key={i}>
              <div className="act-dot" style={{background: actDot(a.event_type)}}/>
              <div>
                <strong>{a.faculty_name || '—'}</strong>{a.note ? ` — ${a.note}` : ''}
              </div>
              <div className="act-time">{a.date}</div>
            </div>
          ))}
        </div>
      </div>

      {drill && <FacultyDrilldown department={drill.department} designation={drill.designation} title={drill.title} onClose={() => setDrill(null)}/>}
    </div>
  );
}
